import type { FareDetailsV2 } from "@b2bportal/air-shopping-api";
import type { FlightFareTransformFn } from "@b2bportal/core-flights";
import type { FlightFareMeta } from "@b2bportal/core-types";

/**
 * Null-safe cash amounts for sorting. Points-based (fixed/grid) fares can have
 * no cash total, and the core `compareFaresByPrice` assumes `total.fiat.value`
 * exists — it throws on such fares. Treat a missing cash amount as 0 so grid
 * fares sort by taxes, then fall back to the fare-shelf tiebreaker below.
 */
const getFareCashAmounts = (
  fare: FareDetailsV2
): { total: number; tax: number } | null => {
  const pricing = fare.paxPricings?.[0]?.pricing;
  if (pricing == null) return null;
  return {
    total: pricing.total?.fiat?.value ?? 0,
    tax: pricing.taxAmount?.fiat?.value ?? 0,
  };
};

/**
 * Custom comparator for RBC that sorts fares by price, then by fare shelf.
 * Mirrors the core price comparison (zero cash total -> sort by taxes) but is
 * null-safe for points-based fares, and adds fare shelf as a tiebreaker.
 */
const compareFaresByPriceAndShelf = (
  fareA: FareDetailsV2,
  fareB: FareDetailsV2
): number => {
  const a = getFareCashAmounts(fareA);
  const b = getFareCashAmounts(fareB);

  let priceComparison: number;
  if (a == null && b == null) {
    priceComparison = 0;
  } else if (a == null) {
    priceComparison = 1;
  } else if (b == null) {
    priceComparison = -1;
  } else {
    // When both cash totals are zero (e.g. points-based pricing), sort by taxes.
    priceComparison =
      a.total === 0 && b.total === 0 ? a.tax - b.tax : a.total - b.total;
  }

  // If prices are equal, sort by fare shelf (lowest to highest)
  // Use the first slice (outbound) for comparison
  if (priceComparison === 0) {
    const fareShelfA = fareA.slices[0]?.fareShelf?.rating ?? Infinity;
    const fareShelfB = fareB.slices[0]?.fareShelf?.rating ?? Infinity;
    return fareShelfA - fareShelfB;
  }

  return priceComparison;
};

/**
 * RBC-specific version of sortFaresWithExtras that sorts by price then fare shelf.
 * Mirrors the core sorting behavior using an RBC-specific, null-safe comparator
 * (see `compareFaresByPriceAndShelf`) with a custom fare shelf tiebreaker.
 */
export const sortFaresWithExtrasRbc: FlightFareTransformFn = (
  rates: FlightFareMeta[]
): FlightFareMeta[] => {
  const flightRates: FlightFareMeta[] = [];
  const extraRatesByFareId = new Map<string, FlightFareMeta[]>();

  // Separate flight rates from extra rates
  for (const rate of rates) {
    if (rate.origin === "flight") {
      flightRates.push(rate);
    } else {
      const fareId = rate.fareDetails.id;
      if (!extraRatesByFareId.has(fareId)) {
        extraRatesByFareId.set(fareId, []);
      }
      extraRatesByFareId.get(fareId)!.push(rate);
    }
  }

  // Sort flight rates by price and fare shelf
  flightRates.sort((a, b) =>
    compareFaresByPriceAndShelf(a.fareDetails, b.fareDetails)
  );

  // Build result: insert extra rates after their matching flight rate
  return flightRates.flatMap((flightRate) => {
    const extraRates = extraRatesByFareId.get(flightRate.fareDetails.id) ?? [];
    return [flightRate, ...extraRates];
  });
};
